import { cn } from '../../lib/utils';

/**
 * FilterChips — katalog Design System (`acm-filter-chips`).
 *
 * Rychlé filtry jedním klikem nad seznamem („Všichni · Zamčení · Vypršelé heslo").
 * Vybraný je vždy právě jeden. Počet u štítku je nepovinný; nula se ukáže tlumeně,
 * ať je vidět, že filtr nic nenajde, ale dá se kliknout.
 */
export interface FilterChipOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

export interface FilterChipsProps<T extends string> {
  options: ReadonlyArray<FilterChipOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Popis skupiny pro čtečky obrazovky. */
  label: string;
  className?: string;
}

export function FilterChips<T extends string>({ options, value, onChange, label, className }: FilterChipsProps<T>) {
  return (
    <div role='radiogroup' aria-label={label} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/60',
              active
                ? 'border-plex/50 bg-plex/18 text-plex'
                : 'border-gray-700 bg-gray-900/60 text-gray-300 hover:border-gray-500 hover:text-gray-100',
            )}
          >
            <span>{option.label}</span>
            {typeof option.count === 'number' && (
              <span
                className={cn(
                  'min-w-5 rounded px-1.5 text-center text-xs tabular-nums',
                  active ? 'bg-plex/25' : option.count === 0 ? 'text-gray-600' : 'bg-gray-800 text-gray-300',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
